import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {usePlannedAccessesFormCounts} from '@/features/planned-accesses/hooks/usePlannedAccessesFormCounts'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {ProjectExtended} from '@/features/projects/types'
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlannedAccessesTotalAbsencesCard} from '@/features/planned-accesses/components/planned-accesses-total-absences-card/PlannedAccessesTotalAbsencesCard'
import {PlannedAccessesAbsenceFields} from '@/features/planned-accesses/components/accesses-form/planned-accesses-absence-fields/PlannedAccessesAbsenceFields'
import {useCallback, useEffect} from 'react'
import {PlannedAccessesElaborationsCard} from '@/features/planned-accesses/components/planned-accesses-elaborations-card/PlannedAccessesElaborationsCard'
import {MedicalReportProject} from '@/features/medical-reports/types'
import {useOutletContext} from 'react-router-dom'
import {PatientExtended} from '@/features/patients/types'

const MedicalReportAbsenceAccessesFieldsSchema = MedicalReportFieldsSchema.pick({
    plannedAccesses: true,
    newProjectDurationDays: true
})
type MedicalReportAbsenceAccessesFieldsSchema = z.infer<typeof MedicalReportAbsenceAccessesFieldsSchema>

const NestedAbsenceAccessFormSchema = z.object({
    absenceAccesses: MedicalReportFieldsSchema.shape.plannedAccesses.shape.absenceAccesses,
    singleAccesses: MedicalReportFieldsSchema.shape.plannedAccesses.shape.singleAccesses
})
type NestedAbsenceAccessFormData = z.infer<typeof NestedAbsenceAccessFormSchema>

export const MedicalReportAbsenceAccessesFields = ({project}: {project: MedicalReportProject}) => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const {control, setValue} = useFormContext<MedicalReportAbsenceAccessesFieldsSchema>()
    const absenceAccesses = useWatch({control, name: 'plannedAccesses.absenceAccesses'})
    const inPersonAccesses = useWatch({control, name: 'plannedAccesses.inPersonAccesses'})
    const singleAccesses = useWatch({control, name: 'plannedAccesses.singleAccesses'})
    const newProjectDurationDays = useWatch({control, name: 'newProjectDurationDays'})

    const {
        data: plannedAccessesData,
        isSuccess,
        isLoading: isPlannedAccessesLoading
    } = useGetPlannedAccesses({
        urlParams: {
            projectId: project.id
        }
    })

    const {
        project: projectExtended,
        totalAbsencesAccessesCount,
        totalProcessingAccesses,
        totalReportingAccessCount,
        totalInPresenceAccessCount,
        unaccountedTotal
    } = usePlannedAccessesFormCounts({
        projectId: project?.id,
        absenceAccesses,
        inPersonAccesses,
        singleAccesses,
        durationDays: newProjectDurationDays
    })

    const handleSetAbsenceAccesses = useCallback(
        (data: ProjectPlannedAccess) => {
            const absenceAccesses = data.plannedAccesses
                .filter(access => access.accessType.groupName === 'non_in_person_accesses')
                .map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses
                }))

            if (absenceAccesses) {
                setValue('plannedAccesses.absenceAccesses', absenceAccesses, {shouldDirty: true})
            }
        },
        [setValue]
    )

    useEffect(() => {
        if (plannedAccessesData && isSuccess) {
            handleSetAbsenceAccesses(plannedAccessesData)
        }
    }, [plannedAccessesData, handleSetAbsenceAccesses, isSuccess])

    return (
        <Flexbox direction="column" gap={6}>
            <PlannedAccessesTotalAbsencesCard
                isLoading={isPlannedAccessesLoading}
                assistiveRegime={project?.assistiveRegime ?? null}
                serviceType={patient?.serviceType ?? null}
                totalInPresenceAccessesCount={totalInPresenceAccessCount}
                totalAbsencesAccessesCount={totalAbsencesAccessesCount}
            />
            {absenceAccesses && absenceAccesses.length > 0 && (
                <MedicalReportAbsenceAccessesWrapperFields
                    totalProcessingAccesses={totalProcessingAccesses}
                    totalReportingAccessCount={totalReportingAccessCount}
                    unaccountedTotal={unaccountedTotal}
                    project={projectExtended}
                />
            )}
        </Flexbox>
    )
}

const MedicalReportAbsenceAccessesWrapperFields = ({
    totalProcessingAccesses,
    totalReportingAccessCount,
    unaccountedTotal,
    project
}: {
    totalProcessingAccesses: number
    totalReportingAccessCount: number
    unaccountedTotal: number
    project: ProjectExtended | undefined
}) => {
    const parentForm = useFormContext<MedicalReportAbsenceAccessesFieldsSchema>()

    const nestedForm = useForm<NestedAbsenceAccessFormData>({
        defaultValues: {
            absenceAccesses: parentForm.watch('plannedAccesses.absenceAccesses'),
            singleAccesses: parentForm.watch('plannedAccesses.singleAccesses')
        }
    })

    // Watch the nested form data and sync with parent
    const nestedAbsenceAccesses = useWatch({
        control: nestedForm.control,
        name: 'absenceAccesses'
    })

    const nestedSingleAccesses = useWatch({
        control: nestedForm.control,
        name: 'singleAccesses'
    })

    useEffect(() => {
        parentForm.setValue('plannedAccesses.absenceAccesses', nestedAbsenceAccesses, {
            shouldDirty: true,
            shouldValidate: true
        })
    }, [nestedAbsenceAccesses])

    useEffect(() => {
        parentForm.setValue('plannedAccesses.singleAccesses', nestedSingleAccesses, {
            shouldDirty: true,
            shouldValidate: true
        })
    }, [nestedSingleAccesses])

    return (
        <FormProvider {...nestedForm}>
            <PlannedAccessesElaborationsCard
                totalProcessingAccesses={totalProcessingAccesses}
                totalReportingAccessCount={totalReportingAccessCount}
                unaccountedTotal={unaccountedTotal}
            />
            <PlannedAccessesAbsenceFields project={project} />
        </FormProvider>
    )
}
